import { ref } from "vue";
import { defineStore } from "pinia";
import type { Organisation } from "~/types";
import { formatHttpError, getErrorStatus, handleInvalidToken } from "~/utils/apiError";

export type { Organisation };

export type OrgMemberRole = "employee" | "owner";

export type StoreWriteResult =
  | { ok: true; refreshed: boolean }
  | { ok: false; message: string; status?: number };

export type InvoiceConfig = {
  show_company_name: boolean;
  show_legal_entity: boolean;
  show_address: boolean;
  show_po_number: boolean;
  show_cost_centre: boolean;
  show_cost_code: boolean;
  show_approver_name: boolean;
  show_custom_invoice_label: boolean;
  default_company_name: string | null;
  default_legal_entity: string | null;
  default_address: Record<string, string | null> | null;
  default_po_number: string | null;
  default_cost_centre_label: string | null;
  default_cost_code: string | null;
  default_approver_name: string | null;
  default_custom_invoice_label: string | null;
};

export const useOrganisationStore = defineStore("organisation", () => {
  const { authFetch, hasToken } = useAuthenticatedFetch();

  // --------------- State ---------------
  const organisation = ref<Organisation | false>(false);
  const organisationLoading = ref(false);
  const invoiceConfig = ref<InvoiceConfig | null>(null);

  // --------------- Actions ---------------

  async function getOrganisation(): Promise<Organisation | null> {
    if (!hasToken()) return null;

    organisationLoading.value = true;
    try {
      const response = await authFetch<Organisation>("/api/customers/organisations");
      organisation.value = response;
      return response;
    } catch (error: unknown) {
      handleInvalidToken(error);
      return null;
    } finally {
      organisationLoading.value = false;
    }
  }

  async function refreshAfterWrite(): Promise<boolean> {
    const fresh = await getOrganisation();
    if (fresh) useAuthStore().setOrganisation(fresh);
    return !!fresh;
  }

  async function failedWrite(error: unknown, fallback: string): Promise<StoreWriteResult> {
    organisationLoading.value = false;
    handleInvalidToken(error);
    const status = getErrorStatus(error);
    if (status && status >= 400 && status < 500 && status !== 401) await refreshAfterWrite();
    return { ok: false, message: formatHttpError(error, fallback), status };
  }

  async function addOrgMember(payload: {
    organisationId: string;
    email: string;
    first_name: string;
    last_name: string;
    role: OrgMemberRole;
    auto_activate?: boolean;
    costCentreIds?: (string | number)[];
  }): Promise<StoreWriteResult> {
    const fallback = "Couldn't add team member, please try again";
    if (!hasToken()) return { ok: false, message: fallback };

    organisationLoading.value = true;
    try {
      await authFetch(`/api/customers/organisations/addCustomer/${payload.organisationId}`, {
        method: "PUT",
        body: {
          email: payload.email,
          first_name: payload.first_name,
          last_name: payload.last_name,
          role: payload.role,
          auto_activate: payload.auto_activate,
          ...(payload.costCentreIds?.length ? { costCentreIds: payload.costCentreIds } : {}),
        },
      });
      return { ok: true, refreshed: await refreshAfterWrite() };
    } catch (error: unknown) {
      return failedWrite(error, fallback);
    }
  }

  async function updateCustomerRole(
    organisationId: string,
    payload:
      | { customerId: string; role: OrgMemberRole }
      | { customerEmail: string; role: OrgMemberRole },
  ): Promise<StoreWriteResult> {
    const fallback = "Couldn't update this member's permission, please try again";
    if (!hasToken()) return { ok: false, message: fallback };

    organisationLoading.value = true;
    try {
      await authFetch(`/api/customers/organisations/${organisationId}/updateRole`, {
        method: "PUT",
        body: payload,
      });
      return { ok: true, refreshed: await refreshAfterWrite() };
    } catch (error: unknown) {
      return failedWrite(error, fallback);
    }
  }

  async function updateCustomerCostCentres(
    customerId: string,
    payload: { costCentreIds: (string | number)[] },
  ): Promise<StoreWriteResult> {
    const fallback = "Couldn't update cost centres, please try again";
    if (!hasToken()) return { ok: false, message: fallback };

    organisationLoading.value = true;
    try {
      await authFetch(`/api/customers/costCentres/customer/${customerId}`, {
        method: "PUT",
        body: { costCentreIds: payload.costCentreIds },
      });
      return { ok: true, refreshed: await refreshAfterWrite() };
    } catch (error: unknown) {
      return failedWrite(error, fallback);
    }
  }

  async function updatePendingCostCentres(
    organisationId: string,
    payload: { customerEmail: string; costCentreIds: (string | number)[] },
  ): Promise<StoreWriteResult> {
    const fallback = "Couldn't update cost centres, please try again";
    if (!hasToken()) return { ok: false, message: fallback };

    organisationLoading.value = true;
    try {
      await authFetch(`/api/customers/organisations/${organisationId}/pendingCostCentres`, {
        method: "PUT",
        body: { customerEmail: payload.customerEmail, costCentreIds: payload.costCentreIds },
      });
      return { ok: true, refreshed: await refreshAfterWrite() };
    } catch (error: unknown) {
      return failedWrite(error, fallback);
    }
  }

  async function removeOrgMember(
    payload:
      | { organisationId: string; email: string }
      | { organisationId: string; pendingOrgCustomerId: number },
  ): Promise<StoreWriteResult> {
    const fallback = "Couldn't remove team member, please try again";
    if (!hasToken()) return { ok: false, message: fallback };

    organisationLoading.value = true;
    try {
      await authFetch(`/api/customers/organisations/removeCustomer/${payload.organisationId}`, {
        method: "PUT",
        body:
          "pendingOrgCustomerId" in payload
            ? { pendingOrgCustomerId: payload.pendingOrgCustomerId }
            : { email: payload.email },
      });
      return { ok: true, refreshed: await refreshAfterWrite() };
    } catch (error: unknown) {
      return failedWrite(error, fallback);
    }
  }

  async function resendInvite(
    organisationId: string,
    pendingOrgCustomerId: number,
  ): Promise<StoreWriteResult> {
    const fallback = "Couldn't resend the invite, please try again.";
    if (!hasToken()) return { ok: false, message: fallback };

    try {
      await authFetch(`/api/customers/organisations/${organisationId}/resendInvite`, {
        method: "PUT",
        body: { pendingOrgCustomerId },
      });
      return { ok: true, refreshed: true };
    } catch (error: unknown) {
      const status = getErrorStatus(error);
      if (status === 404) return failedWrite(error, fallback);
      handleInvalidToken(error);
      return { ok: false, message: formatHttpError(error, fallback), status };
    }
  }

  async function updateOrganisation(
    payload: Record<string, unknown> & { organisationId: string },
  ): Promise<boolean> {
    if (!hasToken()) return false;

    organisationLoading.value = true;
    try {
      await authFetch(`/api/customers/organisations/${payload.organisationId}`, {
        method: "PUT",
        body: payload,
      });
      await refreshAfterWrite();
      return true;
    } catch (error: unknown) {
      organisationLoading.value = false;
      handleInvalidToken(error);
      return false;
    }
  }

  /**
   * Fetches the authed customer's org invoice configuration. Returns implicit defaults when
   * the org has not configured anything (3 customer-identity toggles true, 5 custom-invoice
   * toggles false, all defaults null). The checkout section reads these to decide which rows
   * to show and what to pre-fill.
   */
  async function getInvoiceConfig(organisationId: string | number) {
    if (!hasToken()) return;
    try {
      const response = await authFetch<InvoiceConfig>(
        `/api/customers/organisations/${organisationId}/invoiceConfiguration`,
      );
      invoiceConfig.value = response;
    } catch (error: unknown) {
      handleInvalidToken(error);
    }
  }

  return {
    organisation,
    organisationLoading,
    invoiceConfig,

    getOrganisation,
    addOrgMember,
    removeOrgMember,
    resendInvite,
    updateOrganisation,
    updateCustomerCostCentres,
    updateCustomerRole,
    updatePendingCostCentres,
    getInvoiceConfig,
  };
});
